import { Database } from "@/integrations/supabase/types";

type ConsentLog = Database["public"]["Tables"]["consent_logs"]["Row"];

/**
 * Escapes a CSV field value to handle commas, quotes, and newlines
 */
function escapeCSVField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    // Escape quotes by doubling them and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Extracts language from user agent string
 */
function extractLanguage(userAgent: string | null): string {
  if (!userAgent) return "-";
  const langMatch = userAgent.match(/(?:[a-z]{2}(?:-[A-Z]{2})?)/i);
  return langMatch ? langMatch[0].toLowerCase() : "en";
}

/**
 * Formats a date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Formats status based on granted boolean
 */
function formatStatus(granted: boolean): string {
  return granted ? "Approved" : "Withdrawn";
}

/**
 * Converts consent logs to CSV format
 */
export function exportConsentLogsToCSV(consentLogs: ConsentLog[]): string {
  // CSV Headers
  const headers = [
    "ID",
    "Email",
    "Bundle",
    "Date",
    "Status",
    "IP",
    "Browser",
    "Browser Version",
    "OS",
    "OS Version",
    "Device Type",
    "Device Brand",
    "Device Model",
    "Screen Resolution",
    "Language",
    "Method",
  ];

  // Create header row
  const headerRow = headers.map(escapeCSVField).join(",");

  // Create data rows
  const dataRows = consentLogs.map((log) => {
    const id = log.id.slice(0, 8); // Short ID like in the table
    const email = log.email || "-";
    const bundle = log.policy_version || "-";
    const date = formatDate(log.timestamp);
    const status = formatStatus(log.granted);
    const ip = log.ip_address || "-";
    const browser = log.browser_name || "-";
    const browserVersion = log.browser_version || "-";
    const os = log.os_name || "-";
    const osVersion = log.os_version || "-";
    const deviceType = log.device_type || "-";
    const deviceBrand = log.device_brand || "-";
    const deviceModel = log.device_model || "-";
    const screenResolution =
      log.screen_width && log.screen_height
        ? `${log.screen_width}x${log.screen_height}`
        : "-";
    const language = extractLanguage(log.user_agent);
    const method = log.source_form || "-";

    return [
      escapeCSVField(id),
      escapeCSVField(email),
      escapeCSVField(bundle),
      escapeCSVField(date),
      escapeCSVField(status),
      escapeCSVField(ip),
      escapeCSVField(browser),
      escapeCSVField(browserVersion),
      escapeCSVField(os),
      escapeCSVField(osVersion),
      escapeCSVField(deviceType),
      escapeCSVField(deviceBrand),
      escapeCSVField(deviceModel),
      escapeCSVField(screenResolution),
      escapeCSVField(language),
      escapeCSVField(method),
    ].join(",");
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join("\n");
}

/**
 * Triggers a browser download of the CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

